import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

let loader: GLTFLoader | null = null;

function getLoader(): GLTFLoader {
  if (loader) return loader;
  const draco = new DRACOLoader();
  draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
  loader = new GLTFLoader();
  loader.setDRACOLoader(draco);
  loader.setMeshoptDecoder(MeshoptDecoder);
  return loader;
}

function isGlb(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const magic = new Uint8Array(buffer, 0, 4);
  return String.fromCharCode(...magic) === 'glTF';
}

async function fetchWithProgress(url: string, onProgress: (p: number | null) => void): Promise<ArrayBuffer> {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const total = Number(res.headers.get('content-length')) || 0;
  if (!res.body) return res.arrayBuffer();

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(total ? received / total : null);
  }
  const out = new Uint8Array(received);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out.buffer;
}

export async function parseGlb(buffer: ArrayBuffer): Promise<THREE.Object3D> {
  return parse(buffer);
}

async function parse(buffer: ArrayBuffer): Promise<THREE.Object3D> {
  if (!isGlb(buffer)) throw new Error('The downloaded file is not a .glb model.');
  const gltf = await getLoader().parseAsync(buffer, '');
  return gltf.scene;
}

export async function loadGlbFromSources(urls: string[], onProgress: (p: number | null) => void): Promise<THREE.Object3D> {
  let lastError: unknown = null;
  for (const url of urls) {
    try {
      const buffer = await fetchWithProgress(url, onProgress);
      return await parse(buffer);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError ?? new Error('Unable to load model');
}

export async function loadGlbFromFile(file: File): Promise<THREE.Object3D> {
  return parse(await file.arrayBuffer());
}

/** Scales the model to a printable size, centres it on X/Z and rests it on the platform. */
export function normalizeModel(root: THREE.Object3D, targetSize = 70): THREE.Vector3 {
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  root.scale.multiplyScalar(targetSize / maxDim);

  box.setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());
  root.position.x -= center.x;
  root.position.z -= center.z;
  root.position.y -= box.min.y;

  return new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3());
}

export function disposeObject(root: THREE.Object3D) {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    const original = (mesh.userData.original ?? mesh.material) as THREE.Material | THREE.Material[];
    (Array.isArray(original) ? original : [original]).forEach((m) => m.dispose());
  });
}
